import { CampaignTypeEnum } from '@gr/shared/enums';
import { Button, ButtonVariantEnum } from '@Wonder-Cave/ui';
import { Dialog, DialogBackdrop, DialogDescription, Transition, TransitionChild } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import { Fragment, useEffect, useState } from 'react';
import { convertEnumToReadableString } from '../../providers/utility.provider';

export interface IProviderSwapWarning {
  campaignType: CampaignTypeEnum;
  blockingAreaCodes: string[];
}

interface IProviderSwapWarningModalProps {
  show: boolean;
  // Omitted by the campaign screens (MES-2614): their confirm is Figma-minimal — sentence and
  // buttons only, no per-campaign-type blocking-area-code list.
  warnings?: IProviderSwapWarning[];
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

// Shared by the Clients panel (warnings list + CONFIRM, MES-2587) and the Campaigns grid /
// Campaign Details swaps (no list + UPDATE PROVIDER, MES-2614).
// Built on a portal-local Headless UI Dialog rather than the @Wonder-Cave/ui Modal: on the
// Clients screen this warning opens while the details panel (a z-30 Dialog) is up, and the
// library Modal's z-10 overlay renders underneath it. z-40 keeps the warning on top; that panel
// is made sticky while the warning is open so this dialog's clicks don't count as the panel's
// outside-click (MES-2587). The campaign screens have no such panel — the z-40 Dialog simply
// overlays page content there.
// The panel's geometry (w-full max-w-[696px], 20px corners, shadow-popup), typography (h2 title,
// 16px medium dark-gray body capped at 553px with normal tracking so it wraps like the mock) and
// its own top-right X close follow Figma node 4443:5831 "Confirmation Modal." (MES-2756) — verify
// against that node before changing them, and don't normalise it back to max-w-lg or swap it for
// the library Modal.
const ProviderSwapWarningModal = ({
  show,
  warnings = [],
  confirmLabel = 'CONFIRM',
  onConfirm,
  onCancel,
}: IProviderSwapWarningModalProps) => {
  // The dialog stays open while the confirmed save commits, so disabling on the first Confirm
  // click is the double-submit guard. Reset whenever the dialog (re)opens.
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (show) {
      setConfirming(false);
    }
  }, [show]);

  const handleCancel = () => {
    if (confirming) {
      return;
    }
    onCancel();
  };

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-40 overflow-y-auto" onClose={handleCancel}>
        <div className="flex items-center justify-center min-h-screen px-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
          </TransitionChild>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="relative w-full max-w-[696px] p-6 my-8 bg-white rounded-2.5xl shadow-popup dark:bg-slate-700 dark:text-slate-400">
              <div className="flex justify-end">
                <button
                  type="button"
                  aria-label="Close"
                  disabled={confirming}
                  onClick={handleCancel}
                  className="p-1 text-gray-300 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-25 dark:hover:bg-slate-800 dark:focus:ring-slate-800"
                >
                  <XIcon className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
              <Dialog.Title className="mt-6 text-h2 font-semibold text-center text-black dark:text-white">
                Are you sure?
              </Dialog.Title>
              <DialogDescription
                as="p"
                className="mx-auto mt-6 max-w-[553px] text-base font-medium leading-tight tracking-normal text-center text-dark-gray dark:text-slate-400"
              >
                The new provider doesn't have any phone numbers available in the currently selected preferred area
                codes. Do you want to update anyway?
              </DialogDescription>
              {warnings.length > 0 && (
                <div className="flex flex-col items-center mt-4 space-y-1">
                  {warnings.map((warning) => (
                    <span key={warning.campaignType} className="text-sm text-gray-500 dark:text-slate-400">
                      {warning.campaignType === CampaignTypeEnum.GOTV
                        ? 'GOTV'
                        : convertEnumToReadableString(warning.campaignType)}
                      {warning.blockingAreaCodes.length > 0 ? `: ${warning.blockingAreaCodes.join(', ')}` : ''}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex justify-center mt-9 mb-10">
                <Button
                  variant={ButtonVariantEnum.SECONDARY}
                  disabled={confirming}
                  onClick={handleCancel}
                  className="mr-4 uppercase"
                >
                  CANCEL
                </Button>
                <Button
                  variant={ButtonVariantEnum.PRIMARY}
                  disabled={confirming}
                  onClick={() => {
                    setConfirming(true);
                    onConfirm();
                  }}
                  className="uppercase"
                >
                  {confirmLabel}
                </Button>
              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ProviderSwapWarningModal;
